import type { FieldError } from "./types";

interface ApiFieldError {
  field: string;
  code: string;
  params?: Record<string, unknown>;
}

interface ApiErrorBody {
  errorCode?: string;
  fieldErrors?: ApiFieldError[];
}

interface RtkError {
  status?: number | string;
  data?: unknown;
}

/**
 * Parses an RTK Query/FetchBaseQuery error into a Map keyed by dotted field path.
 *
 * Returns `null` when the error is not a recognizable `ApiErrorDto.ValidationError`
 * (anything other than a 400 carrying `fieldErrors`). Callers use this to tell
 * "server validated the patch and rejected these specific paths" apart from
 * "we have no new information about field errors" (network failure, 500,
 * lens-parsing 400 with no `fieldErrors` array, etc.) — the coordinator's
 * `flush()` clears errors only for paths that were actually sent and merges
 * the new ones in, so a non-validation error must leave the existing map
 * untouched.
 */
export function parseApiFieldErrors(error: unknown): Map<string, FieldError> | null {
  if (!error || typeof error !== "object") return null;

  const rtk = error as RtkError;
  if (rtk.status !== 400) return null;

  const body = rtk.data;
  if (!body || typeof body !== "object") return null;

  const apiErrors = (body as ApiErrorBody).fieldErrors;
  if (!Array.isArray(apiErrors)) return null;

  const result = new Map<string, FieldError>();
  for (const fe of apiErrors) {
    if (typeof fe?.field !== "string" || typeof fe?.code !== "string") continue;
    result.set(fe.field, { code: fe.code, params: fe.params });
  }
  return result;
}
